import { useState } from "react";
import { BadgeCheck, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const fmtDate = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "");

export function PartnerCodeField({ onDone, compact = false }) {
  const { refreshUser } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return setMsg({ ok: false, text: "Enter your Partner Coupon Code." });
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post("/partner/redeem", { code });
      setMsg({ ok: true, text: data.message, pending: data.status === "pending_verification" });
      const u = await refreshUser();
      onDone?.(data, u);
    } catch (err) {
      setMsg({ ok: false, text: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className={compact ? "" : "rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7] p-5"} data-testid="partner-code-form">
      <label htmlFor="partner-code-input" className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6E5549]">Partner Coupon Code</label>
      <p className="mt-1 text-xs text-[#A88B7D]">For MooreBeautyLove 1-Year PMU Accelerator Program partners. No card needed.</p>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <input id="partner-code-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. MBL-NAME-2027" autoComplete="off" spellCheck={false}
          data-testid="partner-code-input" className="h-11 flex-1 rounded-xl border border-[#E6DBCF] bg-white px-4 font-mono text-sm tracking-wider text-[#2C1810] outline-none focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30" />
        <button type="submit" disabled={busy} data-testid="partner-code-submit" className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-[#2C1810] px-6 text-sm font-semibold text-[#FDFBF7] hover:bg-[#3D2317] disabled:opacity-60">
          {busy && <Loader2 size={15} className="animate-spin" />} Redeem code
        </button>
      </div>
      {msg && (
        <p role={msg.ok ? "status" : "alert"} data-testid={msg.ok ? "partner-code-success" : "partner-code-error"}
          className={`mt-3 flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${msg.ok ? (msg.pending ? "bg-[#F7EEDD] text-[#6B5434]" : "bg-[#E7F1EA] text-[#2E6B4F]") : "bg-[#F9ECEC] text-[#8C2A2A]"}`}>
          {msg.ok && !msg.pending && <BadgeCheck size={17} className="mt-0.5 shrink-0" />}{msg.text}
        </p>
      )}
    </form>
  );
}

export function PartnerPlanCard({ partner }) {
  return (
    <div className="rounded-3xl border-2 border-[#C5A059] bg-[#FFFBF2] p-8" data-testid="partner-plan-card">
      <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#9C6218]">PMU Accelerator Partner Access</p>
      <p className="mt-2 font-serif text-3xl font-semibold text-[#2C1810]">Complimentary access</p>
      <p className="mt-2 text-sm text-[#4A3B32]" data-testid="partner-plan-end">Your access is active through <b>{fmtDate(partner.end_date)}</b> (11:59 p.m. Pacific). No card is on file and you won't be charged.</p>
      <p className="mt-2 text-xs text-[#6E5549]">When it ends, your kit and account stay saved and you can choose a paid plan to continue.</p>
    </div>
  );
}
